import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, Stack, CircularProgress,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, Card, CardContent, Chip, Stepper, Step, StepLabel, Paper
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    GetApp as DownloadIcon,
    PlayArrow as RunIcon,
    ArrowBack as BackIcon,
    ListAlt as ListIcon,
    CheckCircle as SuccessIcon
} from '@mui/icons-material';

const RecurringPayrollImportWizard = () => {
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const API_URL = process.env.REACT_APP_API_URL1;
    
    const [wizardStep, setWizardStep] = useState(0);
    const [loadingVal, setLoadingVal] = useState(false);
    const [importing, setImporting] = useState(false);
    const [validationData, setValidationData] = useState(null);
    const [importResult, setImportResult] = useState(null);

    const handleDownloadTemplate = async () => {
        try {
            const res = await axios.get(`${API_URL}/sm/payroll/recurring-import/template`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Recurring_Elements_Import_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Template download error", err);
            alert("Failed to download template");
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoadingVal(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const res = await axios.post(`${API_URL}/sm/payroll/recurring-import/preview`, formData, {
                withCredentials: true,
                headers: { 
                    "X-Tenant": tenant,
                    "Content-Type": "multipart/form-data"
                }
            });
            
            if (res.data.success) {
                setValidationData(res.data.data);
                setWizardStep(1); // Move to validation step
            }
        } catch (err) {
            console.error("Preview error", err);
            alert(err.response?.data?.message || "Failed to preview file");
        } finally {
            setLoadingVal(false);
            e.target.value = null; // reset file input
        }
    };

    const handleExecuteImport = async () => {
        if (!validationData || validationData.stats.invalidCount > 0) {
            alert("Cannot import. Please fix invalid rows first.");
            return;
        }

        setImporting(true);
        try {
            const res = await axios.post(`${API_URL}/sm/payroll/recurring-import/commit`, {
                previewRows: validationData.previewRows,
                fileMetadata: validationData.fileMetadata
            }, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });

            if (res.data.success) {
                setImportResult({ message: res.data.message });
                setWizardStep(2); // Move to success step
            }
        } catch (err) {
            console.error("Commit error", err);
            alert(err.response?.data?.message || "Failed to commit import");
        } finally {
            setImporting(false);
        }
    };

    const resetWizard = () => {
        setWizardStep(0);
        setValidationData(null);
        setImportResult(null);
    };

    return (
        <Box sx={{ p: 4 }}>
            <Stepper activeStep={wizardStep} sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
                <Step><StepLabel>Upload Recurring Allowances/Deductions</StepLabel></Step>
                <Step><StepLabel>Validation & Dry Run</StepLabel></Step>
                <Step><StepLabel>Import Completed</StepLabel></Step>
            </Stepper>

            {/* Step 0: Upload */}
            {wizardStep === 0 && (
                <Box sx={{ textAlign: 'center', py: 6, maxWidth: 680, mx: 'auto' }}>
                    <Alert severity="info" icon={<ListIcon />} sx={{ mb: 4, borderRadius: 3, textAlign: 'left' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Recurring Payroll Import Rules</Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 2 }}>
                            Import long-term recurring allowances and deductions for staff. Supported Actions are ADD, UPDATE, and DELETE. 
                            Valid staff codes and matching Pay Elements are required. Effective dates strictly control payroll processing.
                        </Typography>
                        <Box sx={{ textAlign: 'center' }}>
                            <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2, fontWeight: 700, textTransform: 'none' }}>
                                Download Import Template
                            </Button>
                        </Box>
                    </Alert>

                    {loadingVal ? (
                        <Box sx={{ py: 4 }}>
                            <CircularProgress sx={{ color: '#0f766e', mb: 2 }} />
                            <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Checking employees, pay elements, actions, and dates...</Typography>
                        </Box>
                    ) : (
                        <Box component="label" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #cbd5e1', borderRadius: 5, p: 6, cursor: 'pointer', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9', borderColor: '#0f766e' } }}>
                            <UploadIcon sx={{ fontSize: 60, color: '#94a3b8', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>Upload Recurring Elements Excel or CSV</Typography>
                            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>Use the downloaded template for accuracy.</Typography>
                            <Button variant="contained" component="span" sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>Browse Local Files</Button>
                            <input type="file" hidden accept=".xlsx,.csv" onChange={handleFileChange} />
                        </Box>
                    )}
                </Box>
            )}

            {/* Step 1: Validation */}
            {wizardStep === 1 && validationData && (
                <Box>
                    <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                        <Card sx={{ flex: 1, bgcolor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 4 }}><CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#047857' }}>{validationData.stats.validCount}</Typography>
                            <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>Ready to Import</Typography>
                        </CardContent></Card>
                        <Card sx={{ flex: 1, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}><CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#b91c1c' }}>{validationData.stats.invalidCount}</Typography>
                            <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 700 }}>Invalid Rows (Blocks Import)</Typography>
                        </CardContent></Card>
                    </Stack>

                    {validationData.stats.invalidCount > 0 && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            You cannot proceed with the import because there are invalid rows. Please fix the errors in your file and re-upload.
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Button startIcon={<BackIcon />} onClick={resetWizard} variant="outlined" sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>Back to Upload</Button>
                        <Button startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <RunIcon />} onClick={handleExecuteImport}
                            variant="contained" disabled={importing || validationData.stats.invalidCount > 0 || validationData.stats.validCount === 0}
                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
                            {importing ? 'Executing Import...' : `Commit ${validationData.stats.validCount} Valid Records`}
                        </Button>
                    </Box>

                    <TableContainer component={Paper} sx={{ borderRadius: 4, maxHeight: 600, border: '1px solid #e2e8f0' }}>
                        <Table stickyHeader size="small">
                            <TableHead><TableRow>
                                {['Excel Row', 'Status', 'Employee', 'Action', 'Element', 'Amount', 'Currency', 'Period', 'Validation'].map(label =>
                                    <TableCell key={label} sx={{ fontWeight: 800, bgcolor: '#f1f5f9' }}>{label}</TableCell>)}
                            </TableRow></TableHead>
                            <TableBody>{validationData.previewRows.map(row => (
                                <TableRow key={row.source_row} sx={{ bgcolor: row.is_valid ? 'inherit' : '#fff5f5' }}>
                                    <TableCell sx={{ fontWeight: 800 }}>Row {row.source_row}</TableCell>
                                    <TableCell><Chip label={row.is_valid ? 'Valid' : 'Invalid'} color={row.is_valid ? 'success' : 'error'} size="small" sx={{ fontWeight: 800 }} /></TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.staff_code || '-'}</Typography>
                                        <Typography variant="caption">{row.staff_name || ''}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: row.action === 'DELETE' ? 'error.main' : 'primary.main' }}>{row.action}</TableCell>
                                    <TableCell>{row.element_code}</TableCell>
                                    <TableCell>{row.action !== 'DELETE' ? row.amount : '-'}</TableCell>
                                    <TableCell>{row.action !== 'DELETE' ? row.currency : '-'}</TableCell>
                                    <TableCell>{row.effective_from || '-'} → {row.effective_to || '-'}</TableCell>
                                    <TableCell>
                                        {row.validation_messages.map((msg, idx) => (
                                            <Typography key={idx} variant="caption" color="error" sx={{ display: 'block' }}>• {msg}</Typography>
                                        ))}
                                        {row.is_valid && <Typography variant="caption" color="success.main">OK</Typography>}
                                    </TableCell>
                                </TableRow>
                            ))}</TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Step 2: Success */}
            {wizardStep === 2 && importResult && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <SuccessIcon sx={{ fontSize: 80, color: '#10b981', mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, color: '#064e3b' }}>Import Successfully Committed</Typography>
                    <Typography sx={{ color: '#64748b', mb: 4 }}>{importResult.message}</Typography>
                    <Button variant="outlined" onClick={resetWizard} sx={{ borderRadius: 3, fontWeight: 700, textTransform: 'none' }}>
                        Import Another Batch
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default RecurringPayrollImportWizard;
